import {logger, Mutex, Util} from 'yougile-util'

const TIME_MULTIPLIERS = {
  ms: 1,
  s: 1000,
  m: 60 * 1000,
  h: 60 * 60 * 1000
} as const

export class PromiseUtil {
  static withResolvers<TReturn, TFail = unknown>(): [
    Promise<TReturn>,
    (value: TReturn) => void,
    (reason: TFail) => void
  ] {
    let resolve: (value: TReturn) => void = Util.noop
    let reject: (reason: TFail) => void = Util.noop
    const promise = new Promise<TReturn>((res, rej) => {
      resolve = res
      reject = rej
    })
    return [promise, resolve, reject]
  }

  static startRoutine(
    routine: () => Promise<void> | void,
    interval: number,
    intervalType: keyof typeof TIME_MULTIPLIERS = 'ms'
  ): () => void {
    let isActive = true
    const ms = interval * TIME_MULTIPLIERS[intervalType]
    const run = async (): Promise<void> => {
      while (isActive) {
        try {
          await routine()
        } catch (e) {
          logger.error('PromiseUtil: error in routine', e)
        }
        await Util.wait(ms)
      }
    }
    void run()
    return (): void => {
      isActive = false
    }
  }

  static async runWithTemporaryLock<TRes>(
    timeout: number,
    mutex: Mutex,
    func: () => Promise<TRes>
  ): Promise<TRes> {
    await mutex.lock()
    const unlockTimeout = setTimeout(() => {
      mutex.release()
    }, timeout)
    try {
      return await func()
    } finally {
      clearTimeout(unlockTimeout)
      mutex.release()
    }
  }
}
